
import { createStore, applyMiddleware } from 'redux';
import { syncHistoryWithStore } from 'react-router-redux';
import { browserHistory } from 'react-router';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createEpicMiddleware } from 'redux-observable';
import thunk from 'redux-thunk';
import promise from 'redux-promise-middleware';
import * as Sentry from '@sentry/react';
import timeago from 'timeago.js';
import timeagoES from 'timeago.js/locales/es';
import rootReducer from './reducers/index';
import rootEpic from './epics/index';

const epicMiddleware = createEpicMiddleware(rootEpic);
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options
});

timeago.register('es', timeagoES);

const auth = {};

const genericStore = {
  isLoading: false,
  errors: {
    hasError: false,
    code: '',
    message: '',
    all: {},
  },
};

// create an object for the default data
const defaultState = {
  home: {
    quests: {
      guest: {
        ...genericStore,
        data: [],
        limit: 10,
        total: 0,
        page: 0,
      },
      all: {
        ...genericStore,
        data: [],
        limit: 10,
        total: 0,
        page: 0, // To identify hydtrated navigationss
      },
      ended: {
        ...genericStore,
        data: [],
        limit: 10,
        total: 0,
        page: 0, // To identify hydtrated navigationss
      },
    },
    searchLight: {
      ...genericStore,
      data: [],
    },
    searchLightGameInfo: {
      ...genericStore,
      data: null,
      isLikeUpdating: false,
    },
  },
  auth: {
    twitch: {
      ...genericStore,
    },
    twitter: {
      ...genericStore,
    },
    youtube: {
      isDisconnecting: false,
      ...genericStore,
    },
    steam: {
      ...genericStore,
    },
    isAuthenticated: false,
    rehydrated: false,
    token: '',
    user: {
      connectedAccounts: {
        twitch: {
          id: null,
          username: null,
        },
        youtube: {
          id: null,
          username: null,
        },
        huya: {
          id: null,
          username: null,
        },
        twitter: {
          id: null,
          username: null,
        },
        steam: {
          id: null,
          username: null,
        },
      },
    },
    isUpdating: false,
    ...genericStore,
    ...auth, // This tries to update the token info if token exists in sessionStorage
  },
  config: {
    myPreApprovedGameKeys: {
      ...genericStore,
      isRequestingAGameKey: false,
      data: [],
    },
    myGameKeys: {
      ...genericStore,
      data: [],
    },
    featuredGames: {
      ...genericStore,
      data: [],
    },
    game: {
      ...genericStore,
      data: {},
    },
    games: {
      ...genericStore,
      data: [],
    },
    userGames: {
      ...genericStore,
      data: [],
    },
    quest: {
      ...genericStore,
      isJoiningToAQuest: false,
      isLeavingAQuest: false,
      isCreating: false,
      isShowing: false,
      isVerifyingYoutube: false,
      data: {},
    },
    quests: {
      ...genericStore,
      isJoiningToAQuest: false,
      isLeavingAQuest: false,
      isCreating: false,
      data: [],
    },
    sfgame: {
      ...genericStore,
      data: [],
    },
    stripe: {
      publicKey: process.env.STRIPE_KEY || 'pk_test_51QEmxyFV4wQHDaQmgDo69oUK5es4zGq7jvcKz7JEeJbOqOwYHDqzmcSO1YjDOkadgtUJAjGuHtHU3Y9MP7DM2d0r00oATrNgOj',
    },
    rules: {
      ...genericStore,
      data: [],
    },
    userQuestReferral: {
      ...genericStore,
      data: [],
    },
    storeGames: {
      ...genericStore,
      data: [],
    },
    twitchDrop: {
      ...genericStore,
      data: {},
    },
    announcement: {
      ...genericStore,
      data: null,
    },
  },
  user: {
    myPreApprovedGameKeys: {
      ...genericStore,
      isRequestingAGameKey: false,
      data: [],
    },
    broadcasts: {
      ...genericStore,
      data: [],
    },
    myBounties: {
      ...genericStore,
      isJoiningToABounty: false,
      isLeavingABounty: false,
      data: [],
    },
    myQuests: {
      ...genericStore,
      isJoiningToAQuest: false,
      isLeavingAQuest: false,
      data: [],
    },
    myUserQuests: {
      ...genericStore,
      data: [],
    },
    myPayments: {
      ...genericStore,
      data: [],
    },
    todayActivity: {
      ...genericStore,
      data: [],
    },
    referralsActivity: {
      ...genericStore,
      data: [],
    },
    timedActivity: {
      ...genericStore,
      data: [],
    },
    allUserPayments: {
      ...genericStore,
      data: {},
    },
    tieredActivity: {
      ...genericStore,
      data: [],
    },
    tieredMultipleDaysActivity: {
      ...genericStore,
      data: [],
    },
    link: {
      ...genericStore,
      data: [],
    },
    preSignedUrl: {
      ...genericStore,
      data: [],
    },
    tickets: {
      ...genericStore,
      data: 0,
    },
    wallet: {
      ...genericStore,
      data: 0.00,
    },
    withdraw: {
      ...genericStore,
      data: 0.00,
    },
    rollOuts: {
      ...genericStore,
      data: 0,
    },
    rollOutsCsv: {
      ...genericStore,
      data: 0,
    },
    rollIns: {
      ...genericStore,
      data: [],
    },
    rollInsCsv: {
      ...genericStore,
      data: 0,
    },
    rollOutsAll: {
      ...genericStore,
      data: 0,
    },
    cdkOrder: {
      ...genericStore,
      data: 0.00,
    },
    cdkOrders: {
      ...genericStore,
      data: 0.00,
    },
    cdkOrder_p: {
      ...genericStore,
      data: 0.00,
    },
    orderLink: {
      ...genericStore,
      data: 0,
    },
    cdkOrdersCsv: {
      ...genericStore,
      data: 0.00,
    },
    cdkProducts: {
      ...genericStore,
      data: 0.00,
    },
    twitchDrops: {
      ...genericStore,
      data: [],
    },
    saleProductProperties: {
      ...genericStore,
      data: [],
    },
    saleProductOrder: {
      ...genericStore,
      data: 0.00,
    },
    saleProductOrders: {
      ...genericStore,
      data: 0.00,
    },
    recomSaleProductStatistics: {
      ...genericStore,
      data: {},
    },
    recomSaleProductRank: {
      ...genericStore,
      data: [],
    },
    saleProducts: {
      ...genericStore,
      data: [],
    },
    saleProduct: {
      ...genericStore,
      data: [],
    },
  },
  admin: {
    offPlatform: {
      ...genericStore,
      data: [],
      user: {}
    },
    csvUpload: {
      ...genericStore,
      data: {
        processed: {},
        errors: {},
        creations: {},
      },
    },
    reports: {
      liveStreams: {
        ...genericStore,
        game: {},
        data: [],
      },
      bounties: {
        ...genericStore,
        data: [],
      },
    },
    users: {
      ...genericStore,
      data: [],
      pagination: {},
    },
    usersCsv: {
      ...genericStore,
      data: [],
    },
    user: {
      ...genericStore,
      data: {},
    },
    configs: {
      ...genericStore,
      data: [],
    },
    config: {
      ...genericStore,
      data: [],
    },
    games: {
      ...genericStore,
      data: [],
    },
    game: {
      ...genericStore,
      isCreating: false,
      isUpdating: false,
      data: {},
    },
    platforms: {
      ...genericStore,
      data: [],
    },
    payments: {
      ...genericStore,
      data: {},
      pagination: {},
    },
    paymentsCsv: {
      ...genericStore,
      data: {},
      pagination: {},
    },
    paypalPayments: {
      ...genericStore,
      data: {},
    },
    quests: {
      ...genericStore,
      data: [],
    },
    preSignedUrl: {
      ...genericStore,
      data: {},
    },
    trackedGames: {
      ...genericStore,
      data: [],
    },
    tieredReports: {
      ...genericStore,
      data: [],
    },
    referralReports: {
      ...genericStore,
      data: [],
    },
    rollOuts: {
      ...genericStore,
      data: 0,
    },
    rollOutsCsv: {
      ...genericStore,
      data: 0,
    },
    approve: {
      ...genericStore,
      data: 0,
    },
    rollOutAdmin: {
      ...genericStore,
      data: 0,
    },
    rollOutAdminCsv: {
      ...genericStore,
      data: 0,
    },
    rollInAdmin: {
      ...genericStore,
      data: 0,
    },
    rollInsCsv: {
      ...genericStore,
      data: 0,
    },
    rollOutsAll: {
      ...genericStore,
      data: 0,
    },
    orderLink: {
      ...genericStore,
      data: 0,
    },
    logQuest: {
      ...genericStore,
      data: 0,
    },
    logQuestCsv: {
      ...genericStore,
      data: 0,
    },
    logProfile: {
      ...genericStore,
      data: 0,
    },
    logProfileCsv: {
      ...genericStore,
      data: 0,
    },
    logWallet: {
      ...genericStore,
      data: 0,
    },
    logWalletCsv: {
      ...genericStore,
      data: 0,
    },
    logCdkey: {
      ...genericStore,
      data: 0,
    },
    logCdkeyCsv: {
      ...genericStore,
      data: 0,
    },
    logDailyTask: {
      ...genericStore,
      data: 0,
    },
    logDailyTaskCsv: {
      ...genericStore,
      data: 0,
    },
    logChannel: {
      ...genericStore,
      data: 0,
    },
    logChannelCsv: {
      ...genericStore,
      data: 0,
    },
    logOther: {
      ...genericStore,
      data: 0,
    },
    logOtherCsv: {
      ...genericStore,
      data: 0,
    },
    logSummary: {
      ...genericStore,
      data: 0,
    },
    region: {
      ...genericStore,
      data: 0,
    },
    regions: {
      ...genericStore,
      data: [],
    },
    regionBatch: {
      ...genericStore,
      data: 0,
    },
    region_m: {
      ...genericStore,
      data: 0,
    },
    region_ms: {
      ...genericStore,
      data: 0,
    },
    cdkOrders: {
      ...genericStore,
      data: 0.00,
    },
    cdkOrdersCsv: {
      ...genericStore,
      data: 0.00,
    },
    permissions: {
      ...genericStore,
      data: 0,
    },
    permission_ms: {
      ...genericStore,
      data: 0,
    },
    admins: {
      ...genericStore,
      data: 0,
    },
    admin_ms: {
      ...genericStore,
      data: 0,
    },
    orderLinks: {
      ...genericStore,
      data: 0,
    },
    reconciliationRolls: {
      ...genericStore,
      data: [],
    },
    reconciliationRollsCsv: {
      ...genericStore,
      data: [],
    },
    deduct: {
      ...genericStore,
      data: 0,
    },
    revokeDeduct: {
      ...genericStore,
      data: 0,
    },
    userVisit: {
      ...genericStore,
      data: [],
    },
    cdkRefundCsv: {
      ...genericStore,
      data: [],
    },
    cdkDisputeCsv: {
      ...genericStore,
      data: [],
    },
    tip: {
      ...genericStore,
      data: {},
    },
    saleProduct: {
      ...genericStore,
      data: [],
    },
    uploadSaleProductCDKeys: {
      ...genericStore,
      data: {
        platform: '',
        createdTotal: 0,
        repeatedTotal: 0,
        createdKeys: [],
        repeatedKeys: [],
      },
    },
    saleProductCDKeys: {
      ...genericStore,
      data: [],
      usersWaitingForKeys: [],
    },
    saleProducts: {
      ...genericStore,
      data: [],
    },
    saleProductSetting: {
      ...genericStore,
      data: 0,
    },
    saleProductOrders: {
      ...genericStore,
      data: 0.00,
    },
    saleProductOrder: {
      ...genericStore,
      data: 0.00,
    },
    saleProductOrdersCsv: {
      ...genericStore,
      data: 0.00,
    },
    announcements: {
      ...genericStore,
      data: [],
    },
  },
  commandCenter: {
    games: {
      ...genericStore,
      data: [],
    },
    gameKeys: {
      ...genericStore,
      data: [],
      usersWaitingForKeys: [],
    },
    bountyPredictions: {
      ...genericStore,
      data: [],
    },
    uploadKeys: {
      ...genericStore,
      data: {
        createdTotal: 0,
        repeatedTotal: 0,
        createdKeys: [],
        repeatedKeys: [],
      },
    },
    uploadLinks: {
      ...genericStore,
      data: {
        createdTotal: 0,
        repeatedTotal: 0,
        createdLinks: [],
        repeatedLinks: [],
      },
    },
    linkAssignment: {
      ...genericStore,
      data: {},
    },
    deletedLinks: {
      ...genericStore,
      data: {},
    },
    whiteList: {
      ...genericStore,
      data: [],
    },
    findUsers: {
      ...genericStore,
      data: [],
      total: 0,
      showing: 0,
    },
    gameActivity: {
      ...genericStore,
      data: [],
      total: 0,
    },
    quests: {
      ...genericStore,
      data: [],
      isCreating: false,
    },
    quest: {
      ...genericStore,
      isCreating: false,
      isUpdating: false,
      data: {},
    },
    questActivity: {
      ...genericStore,
      data: [],
    },
    questSales: {
      ...genericStore,
      data: [],
    },
    questTimed: {
      ...genericStore,
      data: [],
    },
    questGeneral: {
      ...genericStore,
      data: [],
      successCount: 0,
      batchErrorMessage: null,
      pagination: {},
    },
    questGeneralCsv: {
      ...genericStore,
      data: [],
      pagination: {},
    },
    questAffiliate: {
      ...genericStore,
      data: [],
    },
    reputationSystem: {
      ...genericStore,
      data: [],
    },
    adminActivityLogs: {
      ...genericStore,
      data: [],
    },
    questTiered: {
      ...genericStore,
      data: [],
      dataReferral: [],
      dataKeysAvailables: 0,
      questSummary: {},
    },
    questTieredMultipleDays: {
      ...genericStore,
      data: [],
    },
    preSignedUrl: {
      ...genericStore,
      data: [],
    },
    campaigns: {
      ...genericStore,
      data: [],
    },
    campaign: {
      ...genericStore,
      isCreating: false,
      isUpdating: false,
      data: {},
    },
    brandUsers: {
      ...genericStore,
      data: [],
    },
    userQuests: {
      ...genericStore,
      data: [],
      revokeBlocked: null,
      revokedCount: 0,
      revokeSeq: 0,
    },
    activeQuests: {
      ...genericStore,
      data: [],
    },
    activeGames: {
      ...genericStore,
      data: [],
    },
    activeUserQuests: {
      ...genericStore,
      isLoadingSpecial: false,
      data: [],
    },
    linksDashboard: {
      ...genericStore,
      data: [],
    },
    questApprove: {
      ...genericStore,
      data: [],
    },
    cdKeyProduct: {
      ...genericStore,
      data: [],
    },
    cdKeyProducts: {
      ...genericStore,
      data: [],
    },
    cdKeyProduct_m: {
      ...genericStore,
      data: 0,
    },
    uploadCDKeys: {
      ...genericStore,
      data: {
        platform: '',
        createdTotal: 0,
        repeatedTotal: 0,
        createdKeys: [],
        repeatedKeys: [],
      },
    },
    CDKeys: {
      ...genericStore,
      data: [],
      usersWaitingForKeys: [],
    },
    CDKeys_d: {
      ...genericStore,
      data: [],
      usersWaitingForKeys: [],
    },
    CDKRecords: {
      ...genericStore,
      data: [],
      usersWaitingForKeys: [],
    },
    twitchDrop: {
      ...genericStore,
      isCreating: false,
      isUpdating: false,
      isCopying: false,
      isDeleting: false,
      data: {},
    },
    twitchDrops: {
      ...genericStore,
      data: [],
      isCreating: false,
    },
  },
  brandUser: {
    campaign: {
      ...genericStore,
      data: {},
    },
    campaigns: {
      ...genericStore,
      data: [],
    },
    deliverable: {
      ...genericStore,
      data: {},
    },
    games: {
      ...genericStore,
      data: [],
    },
    preSignedUrl: {
      ...genericStore,
      data: {},
    },
    quests: {
      ...genericStore,
      data: [],
    },
    streamers: {
      ...genericStore,
      data: [],
    }
  },
};


const composeEnhancers = composeWithDevTools({
  // Specify here name, actionsBlacklist, actionsCreators and other options if needed
});

const shouldLog = process.env.LOG_STATE === true;
const logger = store => next => (action) => {
  if (shouldLog) console.log('dispatching', action);
  const result = next(action);
  if (shouldLog) console.log('next state', store.getState());
  return result;
};

const store = createStore(rootReducer, defaultState, composeEnhancers(
  applyMiddleware(epicMiddleware),
  applyMiddleware(thunk),
  applyMiddleware(promise()),
  sentryReduxEnhancer,
  applyMiddleware(logger),
  // other store enhancers if any
));


export const history = syncHistoryWithStore(browserHistory, store);

export default store;
